import type { ReactNode } from "react";
import { AlertCircle } from "lucide-react";

import { TableCell, TableRow } from "@/shared/ui/table";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";

/**
 * 表格三态收口(2026-07-25 G4 档 D 表格五件套)。
 *
 * ═══ 为什么抽这个 ═══════════════════════════════════════════════════════════
 * 管理域 26 页表格的**表头 / 行 hover / 分页**其实早就统一了(`shared/ui/table` 的
 * `TableHead`/`TableRow` 原语 + `shared/ui/pagination`)。真正各页各写、悄悄漂移的只有
 * 表格体里的**加载 / 错误 / 空**三态:
 *   · padding 三种都有(py-6 / py-8 / py-12)
 *   · 加载态有的带 `fx-skeleton` 有的没有
 *   · `colSpan` 手数,列加减了忘改就会错位
 *   · 空态有的只有一句话、缺「下一步动作」
 * 这个组件把三态收成一处:统一 py-12、统一骨架、colSpan 传入一次、空态支持「一句话 + 动作」。
 *
 * ★这不是把表格卡片化 —— 管理域判据是「表格用于检索」,五件套统一的是**表头/筛选/空态/分页/行 hover**,
 *   不是卡片。本组件只收口五件里的「空态」(以及顺带的加载/错误),表头/行 hover/分页各归其位。
 *
 * ═══ 用法 ═══════════════════════════════════════════════════════════════════
 *   <TableBody>
 *     <DataTableStates
 *       colSpan={10}
 *       isLoading={q.isLoading} isError={q.isError} onRetry={() => q.refetch()}
 *       isEmpty={rows.length === 0} emptyMessage="暂无记录"
 *       emptyAction={<Button>去创建</Button>}
 *     />
 *     {!q.isLoading && !q.isError && rows.map((r) => <TableRow key={r.id}>…</TableRow>)}
 *   </TableBody>
 * 有任一态时渲染一行占满全宽的状态行;三态都不成立时返回 null(调用方渲染真数据)。
 */
export interface DataTableStatesProps {
  /** 表格列数 —— 状态行 colSpan 撑满全宽(列变了只改这一处)。 */
  colSpan: number;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  isEmpty?: boolean;
  /** 空态一句话(动词开头,说清「这里本该有什么」)。 */
  emptyMessage: ReactNode;
  /** 空态下一步动作(可选:如「去创建」)。 */
  emptyAction?: ReactNode;
  /** 加载骨架行数,默认 5。 */
  skeletonRows?: number;
}

export function DataTableStates({
  colSpan,
  isLoading,
  isError,
  errorMessage,
  onRetry,
  isEmpty,
  emptyMessage,
  emptyAction,
  skeletonRows = 5,
}: DataTableStatesProps) {
  if (isLoading) {
    return (
      <>
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <TableRow key={`sk-${i}`} data-testid="table-skeleton-row">
            <TableCell colSpan={colSpan} className="py-3">
              <div className="h-4 w-full fx-skeleton rounded bg-muted" />
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  }

  if (isError) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan} className="py-12 text-center">
          {/* ★错误正文用 text-foreground 不用 text-destructive(门 H-E):红字在深色皮肤对比不足
              (3.3~3.6 < 4.5);错误语义靠红图标传达(不只靠颜色),正文保证高对比可读。 */}
          <div className="flex flex-col items-center gap-2 text-sm text-foreground">
            <span className="inline-flex items-center gap-1.5">
              <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
              {errorMessage || "加载失败,请重试。"}
            </span>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                重试
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  }

  if (isEmpty) {
    return (
      <TableRow>
        <TableCell
          colSpan={colSpan}
          className={cn("py-12 text-center text-sm text-muted-foreground")}
        >
          <div className="flex flex-col items-center gap-3">
            <span>{emptyMessage}</span>
            {emptyAction}
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return null;
}
